<template>
  <v-card
    rounded="lg"
    elevation="3"
    class="pa-2 pa-sm-4">
    <!-- Month Nav -->
    <v-row
      no-gutters
      class="mb-3">
      <v-col
        cols="12"
        sm="6">
        <div class="d-flex align-center flex-wrap">
          <v-btn
            small
            depressed
            color="secondary"
            outlined
            class="mr-2 mb-1"
            style="font-size:12px;"
            @click="goToday">
            Today
          </v-btn>

          <v-btn
            icon
            small
            class="mb-1"
            @click="prevMonth">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-btn
            icon
            small
            class="mb-1"
            @click="nextMonth">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <v-menu
            ref="monthPickerMenu"
            v-model="showMonthPicker"
            :close-on-content-click="false"
            offset-y
            min-width="290px">
            <template #activator="{ on, attrs }">
              <v-btn
                text
                class="font-weight-bold text-none px-2 mb-1"
                :class="$vuetify.breakpoint.xsOnly ? 'text-h6' : 'text-h5'"
                v-bind="attrs"
                v-on="on">
                {{ monthLabel }}
                <v-icon
                  small
                  class="ml-1">
                  mdi-chevron-down
                </v-icon>
              </v-btn>
            </template>
            <v-date-picker
              :value="current.format('YYYY-MM')"
              type="month"
              color="secondary"
              no-title
              @input="onPickMonth" />
          </v-menu>
        </div>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        class="d-flex mt-1 mt-sm-0"
        :class="$vuetify.breakpoint.xsOnly ? 'justify-start' : 'justify-end'">
        <v-btn
          v-if="canCreateEvent"
          depressed
          color="secondary"
          :block="$vuetify.breakpoint.xsOnly"
          @click="$emit('add-event')">
          <v-icon
            small
            left>
            mdi-calendar-plus
          </v-icon>
          Create Event
        </v-btn>
      </v-col>
    </v-row>

    <!-- Day headers -->
    <v-row
      no-gutters
      class="mb-1">
      <v-col
        v-for="d in dayNames"
        :key="d"
        class="text-center"
        style="width:14%; max-width:14%; flex: 0 0 14%;">
        <span class="caption grey--text">{{ d }}</span>
      </v-col>
    </v-row>

    <!-- Calendar Grid -->
    <v-row
      v-for="(week, wi) in calendarWeeks"
      :key="wi"
      no-gutters>
      <v-col
        v-for="(day, di) in week"
        :key="di"
        class="pa-1 calendar-cell"
        style="width:14%; max-width:14%; flex: 0 0 14%;">
        <v-card
          v-if="day"
          flat
          outlined
          :color="value === day.date ? 'grey lighten-3' : 'white'"
          :class="['pa-1', 'd-flex', 'flex-column', 'align-center', { 'elevation-2': value === day.date }]"
          style="height:100%; cursor:pointer; border-radius:10px; overflow:hidden;"
          :style="{ 'border:2px solid #000000 !important': value === day.date }"
          @click="selectDay(day.date)">
          <v-avatar
            :color="isToday(day.date) ? 'secondary' : 'transparent'"
            size="26">
            <span
              :class="isToday(day.date) ? 'white--text' : 'black--text'"
              style="font-size:12px;">{{ day.num }}</span>
          </v-avatar>
          <slot
            name="day-content"
            :date="day.date"
            :day="day" />
        </v-card>

        <div
          v-else
          style="height:100%;" />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: 'MonthCalendar',

  props: {
    value: {
      type: String,
      default: ''
    },
    canCreateEvent: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      today: this.$dayjs(),
      current: this.$dayjs().startOf('month'),
      dayNames: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      showMonthPicker: false
    }
  },

  computed: {
    monthLabel () {
      return this.current.format('MMMM YYYY')
    },

    calendarWeeks () {
      const first = this.current.startOf('month').day()
      const days = this.current.daysInMonth()
      const cells = []
      for (let i = 0; i < first; i++) cells.push(null)
      for (let d = 1; d <= days; d++) {
        const day = this.current.date(d)
        cells.push({ num: d, date: day.format('YYYY-MM-DD') })
      }
      while (cells.length % 7 !== 0) cells.push(null)
      const weeks = []
      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
      return weeks
    }
  },

  methods: {
    onPickMonth (monthStr) {
      this.current = this.$dayjs(`${monthStr}-01`).startOf('month')
      this.showMonthPicker = false
      this.$emit('month-change', this.current.format('YYYY-MM'))
    },

    prevMonth () {
      this.current = this.current.subtract(1, 'month').startOf('month')
      this.$emit('month-change', this.current.format('YYYY-MM'))
    },

    nextMonth () {
      this.current = this.current.add(1, 'month').startOf('month')
      this.$emit('month-change', this.current.format('YYYY-MM'))
    },

    goToday () {
      this.current = this.today.startOf('month')
      this.selectDay(this.today.format('YYYY-MM-DD'))
      this.$emit('month-change', this.current.format('YYYY-MM'))
    },

    selectDay (date) {
      this.$emit('input', date)
    },

    isToday (date) {
      return date === this.today.format('YYYY-MM-DD')
    }
  }
}
</script>

<style scoped>
.calendar-cell {
  height: 130px;
}

@media (max-width: 600px) {
  .calendar-cell {
    height: 130px;
  }
}
</style>
